import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LIVESTOCK_SUMMARY_INDEX } from '../../../router/routes';
import { formatDateValue, route } from '../../../services/utils';
import { LinkComponent } from '../../../components/form/elements/link';
import { faLongArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import css from './summary-view.module.css';
import { useTranslation } from 'react-i18next';
import { useService } from '../../../services/services';
import DryPeriodSummary from './components/reproduction/dry-period-summary';
import InterbreedingSummary from './components/reproduction/interbreeding-summary';
import OptimalInseminationSummary from './components/reproduction/optimal-insemination-summary';
import InseminationIntervalsSummary from './components/reproduction/insemination-intervals-summary';
import { ReproductionInfo } from '../../../interfaces/livestock-reproduction';

export enum TabKey {
  DAIRY = 'dairy',
}

export default function ViewReproduction() {
  const { livestockReproductionService, livestockSummaryService } = useService();
  const { t } = useTranslation();
  const { farmId, id } = useParams<{ farmId: string; id: string }>();

  const SummaryType = {
    SUMMARY_INTERBREEDING_PERIOD: t('pages.livestock.summary.header.summary_interbreeding_period'),
    SUMMARY_PREGNANT_COW: t('pages.livestock.summary.header.summary_pregnant_cow'),
    SUMMARY_NOT_PREGNANT_COW: t('pages.livestock.summary.header.summary_not_pregnant_cow'),
    SUMMARY_INSEMINATION_INTERVALS: t('pages.livestock.summary.header.summary_insemination_intervals'),
    SUMMARY_OPTIMAL_INSEMINATION: t('pages.livestock.summary.header.summary_optimal_insemination'),
    SUMMARY_SHORT_DRY_PERIOD: t('pages.livestock.summary.header.summary_short_dry_period'),
    SUMMARY_LONG_DRY_PERIOD: t('pages.livestock.summary.header.summary_long_dry_period'),
  };

  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [inputValue, setInputValue] = useState<number>(0);
  const [reproductionData, setReproductionData] = useState<ReproductionInfo>({
    shortDryPeriod: [],
    longDryPeriod: [],
    goalBelow: null,
    goalAbove: null,
    dryPeriodChart: [],
    averageInterbreeding: [],
    optimalInsemination: [],
    inseminationIntervals: [],
    greaterInterval: null,
    optimalPeriod: null,
  });

  useEffect(() => {
    livestockReproductionService.summaryType(farmId, id).subscribe(
      (response: any) => {
        setTitle(response.summary);
        setDate(response.current_date);

        livestockSummaryService.getConfigurations(farmId, encodeURIComponent(response.summary)).subscribe(config => {
          setInputValue(config);
        });
      })
  }, [livestockReproductionService, livestockSummaryService, farmId, id]);

  useEffect(() => {
    livestockReproductionService.summaryInfo(farmId, date, title).subscribe(
      (response: ReproductionInfo) => {
        setReproductionData(response);
      })
  }, [livestockReproductionService, farmId, date, title]);

  const handleInputChange = (event: any) => {
    const value = event.target.value.replace(/\D/g, '');
    setInputValue(value);

    livestockSummaryService.saveConfigurations(farmId, encodeURIComponent(title), value).subscribe();
  };

  return (
    <div className={css['field-info']}>
      <div className={css['header']}>
        <LinkComponent
          label="back"
          icon={faLongArrowLeft}
          linkTo={route(LIVESTOCK_SUMMARY_INDEX, {
            farmId: farmId,
            tab: TabKey.DAIRY,
          })}
        />
        <h2>{title ? title : ''}</h2>
      </div>

      <div className={css['top-bar']}>
        <div>
          <span>{t('pages.livestock.summary.section.control_date')}</span>
          <span className={css['strong']}>: {date && formatDateValue(date)} </span>
          {(title === SummaryType.SUMMARY_INTERBREEDING_PERIOD ||
            title === SummaryType.SUMMARY_PREGNANT_COW ||
            title === SummaryType.SUMMARY_NOT_PREGNANT_COW ||
            title === SummaryType.SUMMARY_OPTIMAL_INSEMINATION) && (
              <p>
                {title === SummaryType.SUMMARY_INTERBREEDING_PERIOD ? t('pages.livestock.summary.view.days_goal') : t('pages.livestock.summary.view.percentage_goal')}
                <input
                  className={css['goal-picker']}
                  type="text"
                  value={inputValue}
                  onInput={handleInputChange}
                />
              </p>
            )}
        </div>
      </div>

      {(title === SummaryType.SUMMARY_SHORT_DRY_PERIOD ||
        title === SummaryType.SUMMARY_LONG_DRY_PERIOD) && (
          <DryPeriodSummary reproductionData={reproductionData} title={title} />
        )}

      {title === SummaryType.SUMMARY_INTERBREEDING_PERIOD && (
        <InterbreedingSummary reproductionData={reproductionData} title={title} inputValue={inputValue} />
      )}

      {title === SummaryType.SUMMARY_OPTIMAL_INSEMINATION && (
        <OptimalInseminationSummary reproductionData={reproductionData} title={title} inputValue={inputValue} />
      )}

      {title === SummaryType.SUMMARY_INSEMINATION_INTERVALS && (
        <InseminationIntervalsSummary reproductionData={reproductionData} title={title} />
      )}
    </div>
  );
}